import React, { useEffect, useState, useRef } from 'react';
import { css } from '@emotion/react';
import {
  ScreenFillMethod,
  Statement,
  TransitionStyle,
} from '@/customTypes/chatStory';

import Image from 'next/image';

const PC_MIN_WIDTH = 1024;
export interface IFullScreenEffect {
  statement: Statement;
  type?: 'interactive' | 'storyGame';
}

interface IImageInfo {
  src: string;
  fillMethod: ScreenFillMethod;
  transitionStyle: TransitionStyle;
  isOn: boolean;
  duration: number;
  zIndex: number;
}

const FullScreenEffect = ({ statement, type }: IFullScreenEffect) => {
  const [imageInfo, setImageInfo] = useState<IImageInfo | null>(null);
  const isStoryGame = type === 'storyGame';
  const parentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (statement.statementType === 'FullScreenEffectOn') {
      const { fileLink, fillMethod, transitionStyle, duration } = statement;
      const { storygamePC, storygameMobile, interactive } =
        statement.images || {};

      const isPC = parentRef.current?.offsetWidth
        ? PC_MIN_WIDTH >= parentRef.current.offsetWidth
        : false;

      const imageInfo = {
        src:
          isStoryGame && isPC && storygamePC
            ? storygamePC.link
            : isStoryGame && !isPC && storygameMobile
            ? storygameMobile.link
            : !isStoryGame && interactive
            ? interactive.link
            : fileLink,
        fillMethod:
          isStoryGame && isPC && storygamePC
            ? storygamePC.fillMethod
            : isStoryGame && !isPC && storygameMobile
            ? storygameMobile.fillMethod
            : !isStoryGame && interactive
            ? interactive.fillMethod
            : fillMethod,
        transitionStyle,
        isOn: true,
        duration,
        zIndex: isStoryGame ? 0 : 29,
      };
      setImageInfo(imageInfo);
    } else {
      setImageInfo(
        (prev) =>
          prev && {
            ...prev,
            duration: statement.duration,
            transitionStyle: statement.transitionStyle,
            isOn: false,
          },
      );

      if (statement.transitionStyle === 'None') {
        setImageInfo(null);
      } else {
        setTimeout(() => {
          setImageInfo(null);
        }, statement.duration - 10); // 스타일 duration보다 먼저 끝나야해서 -10
      }
    }
  }, [statement]);

  return (
    <>
      {imageInfo && (
        <div ref={parentRef} css={wrapper(imageInfo)}>
          <div className="image-wrapper">
            {(imageInfo.fillMethod === 'AspectRatioFill' ||
              imageInfo.fillMethod === 'AspectRatio') && (
              <Image
                className="image"
                src={imageInfo.src}
                layout="fill"
                objectFit="contain"
                unoptimized
              />
            )}
            {imageInfo.fillMethod === 'Stretch' && (
              <Image
                className="image"
                src={imageInfo.src}
                layout="fill"
                unoptimized
              />
            )}
            {imageInfo.fillMethod === 'AspectRatioFit' && (
              <Image
                className="image"
                src={imageInfo.src}
                layout="fill"
                objectFit="cover"
                unoptimized
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

const wrapper = ({
  transitionStyle,
  duration,
  isOn,
  zIndex,
}: IImageInfo) => css`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: ${zIndex};

  .image-wrapper {
    position: relative;
    width: 100%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    height: 100%;

    ${transitionStyle === 'Zoom' &&
    (isOn
      ? `animation: zoomIn ${duration}ms ease-in-out;`
      : `animation: zoomOut ${duration}ms ease-in-out;`)}
    ${transitionStyle === 'Fade' &&
    (isOn
      ? `animation: fadeIn ${duration}ms ease-in-out;`
      : `animation: fadeOut ${duration}ms ease-in-out;`)}
  }

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
  @keyframes fadeOut {
    0% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }
  @keyframes zoomIn {
    0% {
      transform: translate(-50%, -50%) scale(0);
    }
    100% {
      transform: translate(-50%, -50%) scale(1);
    }
  }
  @keyframes zoomOut {
    0% {
      transform: translate(-50%, -50%) scale(1);
    }
    100% {
      transform: translate(-50%, -50%) scale(0);
    }
  }
`;

export default FullScreenEffect;
